import { Grid, TextField } from "@material-ui/core";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { CustomNumericFormat } from "../core/CustomNumericFormat";
import { PlaceAutoComplete } from "../../table/filters/PlaceAutocomplete";
import { typeIsOneOfListingTypes } from "utils/listings/helper";
import type { SyntheticEvent, PropsWithChildren } from "react";
import type { PlaceAutoCompleteIds } from "../../table/filters/PlaceAutocomplete";
import type { CreateAddressDetails } from "components/_dashboard/listingsMatch/CreateAddressDialog";
import type { EditableListing, EditableListingIds } from "../../../../../@types/listings/new";

type CustomComponentInputProps = {
  customCheck?: () => boolean;
  customComponent: JSX.Element;
};

type CustomComponentInput = Record<
  Extract<EditableListingIds, "fee" | "apartmentNumber">,
  CustomComponentInputProps
>;

type ListingDialogTextsProps = PropsWithChildren<{
  loading: boolean;
  editableFormColumns: EditableListing;
  customComponentsInput: CustomComponentInput;
  handleChange: ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => void;
  handleAutoCompleteFill: (data: Partial<CreateAddressDetails>) => void;
  handleAutoCompleteChange: (
    type: PlaceAutoCompleteIds
  ) => (_event: SyntheticEvent, value: string) => void;
}>;

export function ListingDialogInputTexts({
  loading,
  editableFormColumns,
  customComponentsInput,
  handleChange,
  handleAutoCompleteFill,
  handleAutoCompleteChange
}: ListingDialogTextsProps): JSX.Element {
  const { t: translate } = useTranslation();

  return (
    <>
      {editableColumns.map(
        ({ id, type, required, autoComplete, customComponent, customLabel, customCheck }) => {
          if (customComponent) {
            const customComponentInput = customComponentsInput[id];

            if (!customComponentInput) return null;

            const { customCheck, customComponent } = customComponentInput;

            const showCustomComponent = customCheck ? customCheck() : true;

            if (!showCustomComponent) return null;

            return (
              <Grid item xs={12} sm={6} md={4} key={id}>
                {customComponent}
              </Grid>
            );
          }

          const shouldShowField = customCheck ? customCheck(editableFormColumns) : true;

          if (!shouldShowField) return null;

          const label = translate(
            `listingsPage.tableColumns.${customLabel?.(editableFormColumns) ?? id}`
          );

          const value = editableFormColumns[id];

          return (
            <Grid item xs={12} sm={6} md={4} key={id}>
              {type === "date" ? (
                <TextField
                  fullWidth
                  type="date"
                  name={id}
                  label={label}
                  value={value}
                  disabled={loading}
                  required={required}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                />
              ) : type === "number" ? (
                <TextField
                  fullWidth
                  disabled={loading}
                  label={label}
                  value={value}
                  name={id}
                  required={required}
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: CustomNumericFormat as never
                  }}
                />
              ) : autoComplete ? (
                <PlaceAutoComplete
                  type={id as PlaceAutoCompleteIds}
                  disabled={loading}
                  required={required}
                  inputValue={value as string}
                  handleInputChange={handleAutoCompleteChange(id as PlaceAutoCompleteIds)}
                  handleAutoCompleteFill={handleAutoCompleteFill}
                />
              ) : (
                <TextField
                  name={id}
                  type="text"
                  fullWidth
                  label={label}
                  value={value}
                  required={required}
                  disabled={loading}
                  onChange={handleChange}
                />
              )}
            </Grid>
          );
        }
      )}
    </>
  );
}

type EditableColumns = {
  id: EditableListingIds;
  type: "text" | "number" | "date" | "textarea";
  required: boolean;
  autoComplete?: boolean;
  customComponent?: boolean;
  customLabel?: (editableListing: EditableListing) => string | null;
  customCheck?: (editableListing: EditableListing) => boolean;
};

const editableColumns: EditableColumns[] = [
  {
    id: "streetNumber",
    type: "number",
    required: false
  },
  {
    id: "apartmentNumber",
    type: "number",
    required: false,
    customComponent: true
  },
  {
    id: "addressAreaNickname",
    type: "text",
    required: false
  },
  {
    id: "locationDetails",
    type: "text",
    required: false
  },
  {
    id: "price",
    type: "number",
    required: true
  },
  {
    id: "rooms",
    type: "number",
    required: false
  },
  {
    id: "sizeOfPropertyM2",
    type: "number",
    required: false,
    customCheck: ({ listingType }) => !typeIsOneOfListingTypes(listingType, ["lot"])
  },
  {
    id: "sizeOfPlotM2",
    type: "number",
    required: false,
    customCheck: ({ listingType }) =>
      typeIsOneOfListingTypes(listingType, [
        "privateHouse",
        "semiDetached",
        "farm",
        "lot",
        "warehouses"
      ])
  },
  {
    id: "exclusivity",
    type: "date",
    required: false
  },
  {
    id: "fee",
    type: "number",
    required: false,
    customComponent: true
  },
  {
    id: "cleaningFee",
    type: "number",
    required: false
  },
  {
    id: "municipalityTaxes",
    type: "number",
    required: false
  }
];

// function handleCheckAreaAndPlotSize(
//   id: Extract<EditableListingIds, "sizeOfPlotM2" | "sizeOfPropertyM2">,
//   { listingType }: EditableListing
// ): boolean {
//   const showBothFields = typeIsOneOfListingTypes(listingType, ["privateHouse", "semiDetached"]);

// }
